import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import lodashCompact from 'lodash/fp/compact';

import {
  makeListingAddressStreetDisplay,
} from 'common/listingHelpers';

import {
  ACCOUNT_TYPE_BANK,
  ACCOUNT_TYPE_CREDIT_DEBIT_CARD,
} from 'common/financeConstants';

import {
  STRIPE_CHARGE_STATUS_SUCCESS,
} from 'common/stripeConstants';

import { stateOptionItems } from 'common/util/stateCodes';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  SR_API_ACTION_CONNECT_BANK_ACCOUNT,
  SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
  SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
} from 'api/actionTypes';

import log from 'client/lib/log';

import {
  extractListingAddressFieldsFromGooglePlace,
} from 'client/lib/googlePlaces';

import {
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import withApiState from 'client/helpers/withApiState';
import postWithState from 'client/actions/postWithState';
import {
  BUTTON_THEME_GREEN_ADOBE,
} from 'client/components/ButtonDeprecated';
import {
  SR_BUTTON_THEME_ADOBE_YELLOW,
} from 'client/components/SrButton/SrButton';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrMultiStepForm, {
  SR_MULTI_STEP_FORM_THEME_PARTNER_PAGE,
} from 'client/components/SrMultiStepForm';
import {
  SR_CHECKBOX_LIST_THEME_ICON_BOX,
} from 'client/components/Checkbox';
// import SrConnectBankAccountFormStepSimple from './SrConnectBankAccountFormStepSimple';
import SrConnectBankAccountFormStepConnect from './SrConnectBankAccountFormStepConnect';
import SrConnectBankAccountFormFooter from './SrConnectBankAccountFormFooter';
import SrConnectBankAccountFormSummary from './SrConnectBankAccountFormSummary';
import SrConnectBankAccountFormStepAccountHolder from './SrConnectBankAccountFormStepAccountHolder';

function getFormSteps() {
  const formSteps = [
    {
      id: 'chooseBank',
      component: SrConnectBankAccountFormStepConnect,
      ui: {
        navbarTitle: 'Connect Bank',
        pageHeader: 'Choose Bank',
        pageIntroText: 'Where do you want the funds deposited?',
        controlComponent: null,
      },
      fieldIds: [
        'accountType',
        'stripeAccountToken',
      ],
    },
    {
      id: 'accountHolder',
      component: SrConnectBankAccountFormStepAccountHolder,
      ui: {
        navbarTitle: 'Connect Bank',
        pageHeader: 'Account Holder',
        pageIntroText: 'This is the contact info associated with the bank account.',
      },
      fieldIds: [
        'firstName',
        'lastName',
        'email',
        'phone',
        'dob',
        'ssnLast4',
        'addressStreetDisplay',
        'addressStreetNumber',
        'addressStreetName',
        'addressCity',
        'addressStateCode',
        'addressZip',
        'locationLatitude',
        'locationLongitude',
        'addressUnitNumber',
      ],
    },
    {
      id: 'checkout',
      component: SrConnectBankAccountFormSummary,
      ui: {
        navbarTitle: 'Connect Bank',
        pageHeader: 'Review & Connect',
        pageIntroText: (
          'You’re almost ready to collect funds. Please check that this information is correct.'
        ),
        controlButtonText: 'Submit & Connect Bank',
        controlButtonTheme: SR_BUTTON_THEME_ADOBE_YELLOW,
      },
      fieldIds: [],
    },
  ].map((formStep) => ({
    ...formStep,
    ui: {
      theme: SR_MULTI_STEP_FORM_THEME_PARTNER_PAGE,
      controlComponent: SrConnectBankAccountFormFooter,
      ...formStep.ui,
    },
  }));

  return formSteps;
}

function makeFormFieldsGetterFunc({
  googleAddressMatches,
  onGoogleAddressChange,
  onFindGoogleListings,
}) {
  return ({ values = {} } = {}) => {
    return [
      {
        id: 'accountType',
        title: 'Choose Account',
        type: 'multiple_choice',
        properties: {
          choices: [
            {
              label: 'Debit Card',
              value: ACCOUNT_TYPE_CREDIT_DEBIT_CARD,
              iconUrl: '/images/credit-cards-grey.svg',
              activeIconUrl: '/images/credit-cards-green.svg',
            },
            {
              label: 'Bank Account',
              value: ACCOUNT_TYPE_BANK,
              iconUrl: '/images/bank-accounts-grey.svg',
              activeIconUrl: '/images/bank-accounts-green.svg',
            },
          ],
          multipleSelection: false,
          fieldType: SR_CHECKBOX_LIST_THEME_ICON_BOX,
        },
        margin: '0',
      },
      {
        id: 'stripeAccountToken',
        type: 'hidden',
      },
      {
        id: 'cardExpiration',
        type: 'hidden',
      },
      {
        id: 'cardLast4',
        type: 'hidden',
      },
      {
        id: 'bankAccountLast4',
        type: 'hidden',
      },
      {
        id: 'bankRoutingLast4',
        type: 'hidden',
      },
      {
        id: 'firstName',
        title: 'Legal First Name',
        type: 'short_text',
        properties: {
          placeholder: 'Write here...',
        },
        validations: ['required'],
      },
      {
        id: 'lastName',
        title: 'Legal Last Name',
        type: 'short_text',
        properties: {
          placeholder: 'Write here...',
        },
        validations: ['required'],
      },
      {
        id: 'email',
        title: 'Email',
        type: 'email',
        properties: {
          placeholder: 'Write here...',
        },
        validations: ['email', 'required'],
      },
      {
        id: 'phone',
        title: 'Phone',
        type: 'phone',
        properties: {
          placeholder: 'Write here...',
          pattern: '[0-9]*',
        },
        validations: ['phone', 'required'],
      },
      {
        id: 'dob',
        title: 'Date of Birth',
        type: 'date_input',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: false,
        },
        validations: ['required', 'dateOfBirth'],
      },
      {
        id: 'ssnLast4',
        title: 'Last 4 Digits of SSN',
        type: 'ssn',
        properties: {
          placeholder: '8888',
          pattern: '[0-9]*',
        },
        validations: ['required', 'digits4'],
      },
      {
        id: 'manualAddressInput',
        type: 'hidden',
      },
      hideFieldIf(values.manualAddressInput, {
        id: 'addressStreetDisplay',
        title: 'Billing Address',
        label: '(must select dropdown option)',
        subtitle: 'US address only. PO Boxes not allowed.',
        type: 'autocomplete',
        validations: lodashCompact([
          'required',
          (!values.locationLatitude || !values.locationLongitude) && 'useManualAddressInput',
        ]),
        properties: {
          placeholder: 'Address',
          minCharMatchCount: 1,
          choices: googleAddressMatches.map(({ description }) => description),
          onSuggestionsFetchRequested: ({ value }) => {
            return onFindGoogleListings({ ...values, addressStreetDisplay: value });
          },
          onChange: onGoogleAddressChange,
          filterFunc: () => {
            return true;
          },
        },
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressStreetNumber',
        type: 'short_text',
        title: 'Street Number',
        label: '(Billing Address)',
        subtitle: 'US address only. PO Boxes not allowed.',
        properties: {
          placeholder: 'Street Number',
        },
        validations: ['required'],
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressStreetName',
        type: 'short_text',
        title: 'Street Name',
        label: '(Billing Address)',
        subtitle: 'US address only. PO Boxes not allowed.',
        properties: {
          placeholder: 'Street Name',
        },
        validations: ['required'],
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressCity',
        type: 'short_text',
        title: 'City',
        properties: {
          placeholder: 'City',
        },
        validations: ['required'],
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressStateCode',
        type: 'select',
        title: 'State',
        properties: {
          choices: stateOptionItems,
          enableResetOption: true,
        },
        validations: ['required'],
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressZip',
        type: 'number',
        title: 'ZIP Code',
        properties: {
          placeholder: 'ZIP Code',
        },
        validations: ['required'],
      }),
      {
        id: 'locationLatitude',
        type: 'hidden',
      },
      {
        id: 'locationLongitude',
        type: 'hidden',
      },
      {
        id: 'addressUnitNumber',
        title: 'Unit #',
        label: '(optional)',
        type: 'short_text',
        properties: {
          placeholder: 'Unit #',
        },
      },
      {
        id: 'listingId',
        type: 'hidden',
      },
      {
        id: 'partnerId',
        type: 'hidden',
      },
      {
        id: 'household',
        type: 'hidden',
      },
    ];
  };
}

@withApiState({
  connectBankAccountApiState: {
    action: SR_API_ACTION_CONNECT_BANK_ACCOUNT,
  },
})
class SrConnectBankAccountForm extends Component {
  static propTypes = {
    onBack: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    listingId: PropTypes.number,
    partnerId: PropTypes.number,
    household: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    connectBankAccountApiState: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      googleAddressMatches: [],
      shouldShowSuccessModal: false,
    };
    this._onFindGoogleListings = this._onFindGoogleListings.bind(this);
  }

  _onSubmit = (formValues) => {
    const { dispatch } = this.props;
    const dobMoment = moment(formValues.dob);
    const connectBankAccountPayload = {
      listingId: formValues.listingId || null,
      partnerId: formValues.partnerId || null,
      account: setNullEmptyValues({
        type: formValues.accountType,
        stripeAccountToken: formValues.stripeAccountToken,
        displayName: `${formValues.firstName} ${formValues.lastName}`,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        phone: formValues.phone,
        dob: formValues.dob,
        dobDay: dobMoment.date(),
        dobMonth: dobMoment.month() + 1,
        dobYear: dobMoment.year(),
        ssnLast4: formValues.ssnLast4,
        addressStreetNumber: formValues.addressStreetNumber,
        addressStreetName: formValues.addressStreetName,
        addressCity: formValues.addressCity,
        addressStateCode: formValues.addressStateCode,
        addressZip: formValues.addressZip,
        addressUnitNumber: formValues.addressUnitNumber,
        addressStreetDisplay: makeListingAddressStreetDisplay(formValues),
        cardExpiration: formValues.cardExpiration,
        cardLast4: formValues.cardLast4,
        bankAccountLast4: formValues.bankAccountLast4,
        bankRoutingLast4: formValues.bankRoutingLast4,
      }),
    };
    dispatch(postWithState, {
      action: SR_API_ACTION_CONNECT_BANK_ACCOUNT,
      payload: connectBankAccountPayload,
      onSuccess: () => {
        this.setState({
          shouldShowSuccessModal: true,
        });
      },
      onError: (error) => {
        global.scrollTo(0, global.document.body.scrollHeight);
        log.error(error);
      },
    });
  }

  _onViewMyListingButtonClick = () => {
    const { onSubmit } = this.props;
    onSubmit();
  }

  _onFindGoogleListings(formValues) {
    const { dispatch } = this.props;
    if (
      !formValues.addressStreetDisplay
    ) {
      this.setState({
        googleAddressMatches: [],
      });
      return;
    }

    dispatch(postWithState, {
      queryId: formValues.addressStreetDisplay,
      action: SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
      payload: {
        input: formValues.addressStreetDisplay,
        limitToTexas: false,
      },
    })
      .then((autocompletePredictions) => {
        this.setState({
          googleAddressMatches: autocompletePredictions || [],
        });
      });
  }

  _onGoogleAddressChange = ({ newValue, formikBag }) => {
    const { googleAddressMatches } = this.state;
    const selectedMatch = (
      googleAddressMatches?.find(({ description }) => description === newValue)
    );
    if (selectedMatch?.place_id) {
      this._loadMatchAddressIntoForm({
        placeId: selectedMatch.place_id,
        formikBag,
      });
    }
    else {
      formikBag.setFieldValue('addressStreetNumber', '');
      formikBag.setFieldValue('addressStreetName', '');
      formikBag.setFieldValue('addressCity', '');
      formikBag.setFieldValue('addressStateCode', '');
      formikBag.setFieldValue('addressZip', '');
      formikBag.setFieldValue('locationLatitude', '');
      formikBag.setFieldValue('locationLongitude', '');
    }
  }

  _loadMatchAddressIntoForm = ({ placeId, formikBag }) => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
      payload: {
        placeId,
      },
    })
      .then((googlePlace) => {
        const listingMatch = extractListingAddressFieldsFromGooglePlace(googlePlace);
        if (listingMatch) {
          formikBag.setFieldValue('addressStreetNumber', listingMatch.addressStreetNumber);
          formikBag.setFieldValue('addressStreetName', listingMatch.addressStreetName);
          formikBag.setFieldValue('addressCity', listingMatch.addressCity);
          formikBag.setFieldValue('addressStateCode', listingMatch.addressState);
          formikBag.setFieldValue('addressZip', listingMatch.addressZip);
          formikBag.setFieldValue('locationLatitude', parseFloat(listingMatch.locationLatitude) || '');
          formikBag.setFieldValue('locationLongitude', parseFloat(listingMatch.locationLongitude) || '');
        }
      });
  }

  _getSuccessModalProps = () => {
    const {
      household,
    } = this.props;

    let headline = 'Thank you for connecting your bank!';
    let message = 'You\'ll be notified when a renter pays & the funds are deposited.';

    if (household) {
      if (household.securityDepositCharge?.status === STRIPE_CHARGE_STATUS_SUCCESS) {
        headline = 'Transfer Initiated!';
        message = 'The funds will be transfered to your account within the next 2-3 biz days.';
      }
      else {
        headline = 'Transfer Scheduled!';
        message = (
          'Once the renter’s payment has cleared, ' +
          'the funds will be transfered to your account within the next 2-3 biz days.'
        );
      }
    }

    return {
      headline,
      message,
    };
  }

  render() {
    const {
      onBack,
      listingId,
      partnerId,
      household,
      dispatch,
      connectBankAccountApiState,
    } = this.props;

    const {
      googleAddressMatches,
      shouldShowSuccessModal,
    } = this.state;

    const getFormFields = makeFormFieldsGetterFunc({
      googleAddressMatches: googleAddressMatches,
      onGoogleAddressChange: this._onGoogleAddressChange,
      onFindGoogleListings: this._onFindGoogleListings,
    });

    return (
      <>
        <SrMultiStepForm
          getFormSteps={getFormSteps}
          getFormFields={getFormFields}
          onBack={onBack}
          onSubmit={this._onSubmit}
          defaultValues={{
            listingId: listingId,
            partnerId: partnerId,
            household: household,
            accountType: ACCOUNT_TYPE_CREDIT_DEBIT_CARD,
          }}
          confirmLeave={!shouldShowSuccessModal}
          dispatch={dispatch}
          isBusy={connectBankAccountApiState.inProgress || shouldShowSuccessModal}
          submitErrorMessage={connectBankAccountApiState.error?.message}
        />
        {shouldShowSuccessModal && (
          <SrSimpleMessageModal
            {...this._getSuccessModalProps()}
            iconUrl='/images/checkmark-circle-large-green.svg'
            buttonTheme={BUTTON_THEME_GREEN_ADOBE}
            buttonText='Ok, I understand'
            onButtonClick={this._onViewMyListingButtonClick}
            maxWidth='375'
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            underline
          />
        )}
      </>
    );
  }
}

SrConnectBankAccountForm.displayName = 'SrConnectBankAccountForm';

export default SrConnectBankAccountForm;
